import React, { useState, useEffect } from 'react';

import {
    Typography,
    Box,
    Divider,
    ToggleButtonGroup,
    ToggleButton,
    List,
    CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { useAppDispatch, useAppSelector } from 'hooks';

import { getOrganisationList } from 'state/organisations';

import StandardPage from 'components/StandardPage';
import ListItem from 'components/user/ListItem';

interface Props {}

const OrganisationPage: React.FunctionComponent<Props> = ({}) => {
    const theme = useTheme();
    const dispatch = useAppDispatch();

    const { auth, organisations } = useAppSelector((state) => state);

    const [selectedOrgId, setSelectedOrgId] = useState<number>();

    const userOrgs = auth.user.object?.organisation_set;

    const org = organisations.list.object?.find((o) => o.id === selectedOrgId);

    useEffect(() => {
        if (auth.user.status === 'finished' && auth.user.object) {
            setSelectedOrgId(auth.user.object.organisation_set[0]?.id);
        }
    }, [auth.user]);

    useEffect(() => {
        if (auth.tokens.status === 'finished') {
            dispatch(getOrganisationList());
        }
    }, [auth.tokens]);

    return (
        <StandardPage
            title="My Organisation"
            breadcrumbs={[
                { text: 'Home', link: '/' },
                { text: 'My Organisation' },
            ]}
            maxWidth="xl"
        >
            <Box
                sx={{
                    backgroundColor: theme.palette.common.neutralXXLight,
                    borderRadius: 1,
                    p: 1,
                }}
            >
                {organisations.list.status === 'loading' && (
                    <Box>
                        <CircularProgress />
                    </Box>
                )}
                {org && (
                    <>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <Typography variant="h4">{org?.name}</Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    flexWrap: 'wrap',
                                }}
                            >
                                {userOrgs && userOrgs.length > 1 && (
                                    <ToggleButtonGroup
                                        value={selectedOrgId}
                                        exclusive
                                        onChange={(_, value) =>
                                            setSelectedOrgId(value)
                                        }
                                    >
                                        {userOrgs?.map((otherOrg) => (
                                            <ToggleButton
                                                key={otherOrg.id}
                                                value={otherOrg.id}
                                            >
                                                {otherOrg.name}
                                            </ToggleButton>
                                        ))}
                                    </ToggleButtonGroup>
                                )}
                            </Box>
                        </Box>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle1" sx={{ pb: 2 }}>
                            {org?.description}
                        </Typography>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplate: '1fr 1fr / auto auto',
                            }}
                        >
                            <Typography variant="subtitle1">
                                Address:
                            </Typography>
                            <Typography variant="subtitle1">
                                {org?.address}
                            </Typography>
                            <Typography variant="subtitle1">
                                Contact Email:
                            </Typography>
                            <Typography variant="subtitle1">
                                {org?.primaryContactEmail}
                            </Typography>
                        </Box>
                        <Divider sx={{ my: 1 }} />
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: 2,
                            }}
                        >
                            <Box>
                                <Typography variant="h6">Users</Typography>
                                <List sx={{ width: '100%' }}>
                                    {org.users.map((user) => (
                                        <ListItem key={user.id} user={user} />
                                    ))}
                                </List>
                            </Box>
                        </Box>
                    </>
                )}
            </Box>
        </StandardPage>
    );
};

OrganisationPage.displayName = 'OrganisationPage';
export default OrganisationPage;
